/**
 * Natural dates (SPEC §7.2): "today", "yesterday", "last tuesday", "3 weeks ago", "March",
 * "Oct 9", "2026-03-15", "last week", … → an inclusive range of days. Days are local calendar
 * days as strings (SPEC D21); arithmetic runs in UTC so DST never shifts a day.
 */

export interface DayRange {
  /** Inclusive, YYYY-MM-DD. */
  readonly from: string
  readonly to: string
}

const MONTHS = ['january', 'february', 'march', 'april', 'may', 'june', 'july', 'august', 'september', 'october', 'november', 'december']
const WEEKDAYS = ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday']

const pad = (n: number): string => String(n).padStart(2, '0')

function toUtc(day: string): Date {
  const [y, m, d] = day.split('-').map(Number) as [number, number, number]
  return new Date(Date.UTC(y, m - 1, d))
}

function fromUtc(d: Date): string {
  return `${d.getUTCFullYear()}-${pad(d.getUTCMonth() + 1)}-${pad(d.getUTCDate())}`
}

export function addDays(day: string, n: number): string {
  const d = toUtc(day)
  d.setUTCDate(d.getUTCDate() + n)
  return fromUtc(d)
}

export function addMonths(day: string, n: number): string {
  const d = toUtc(day)
  const target = d.getUTCMonth() + n
  const last = new Date(Date.UTC(d.getUTCFullYear(), target + 1, 0)).getUTCDate()
  return fromUtc(new Date(Date.UTC(d.getUTCFullYear(), target, Math.min(d.getUTCDate(), last))))
}

/** The local calendar day of a timestamp (the device's time zone). */
export function localDay(ms: number = Date.now()): string {
  const d = new Date(ms)
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`
}

const weekday = (day: string): number => toUtc(day).getUTCDay()
const one = (day: string): DayRange => ({ from: day, to: day })
const monthRange = (y: number, m: number): DayRange => ({
  from: `${y}-${pad(m)}-01`,
  to: fromUtc(new Date(Date.UTC(y, m, 0))),
})
/** Monday-based weeks (ISO). */
const weekOf = (day: string): DayRange => {
  const from = addDays(day, -((weekday(day) + 6) % 7))
  return { from, to: addDays(from, 6) }
}

function monthIndex(word: string): number {
  if (word.length < 3) return -1
  return MONTHS.findIndex((m) => m.startsWith(word))
}

function weekdayIndex(word: string): number {
  if (word.length < 3) return -1
  return WEEKDAYS.findIndex((w) => w.startsWith(word))
}

function validDay(y: number, m: number, d: number): string | null {
  if (m < 1 || m > 12 || d < 1) return null
  const last = new Date(Date.UTC(y, m, 0)).getUTCDate()
  return d > last ? null : `${y}-${pad(m)}-${pad(d)}`
}

/** A day in the year nearest to `today` (Dec 5 in October is this December; Jan 5 is next January). */
function nearestYear(m: number, d: number, today: string): string | null {
  const y = Number(today.slice(0, 4))
  let best: string | null = null
  let bestDist = Infinity
  for (const yy of [y - 1, y, y + 1]) {
    const day = validDay(yy, m, d)
    if (!day) continue
    const dist = Math.abs(toUtc(day).getTime() - toUtc(today).getTime())
    if (dist < bestDist) {
      best = day
      bestDist = dist
    }
  }
  return best
}

/** A span like "30d", "2w", "3m", "1y" in days (months as 30, years as 365), or null. */
export function parseSpan(s: string): number | null {
  const m = /^(\d{1,4})\s*(d|days?|w|weeks?|m|months?|y|years?)$/i.exec(s.trim())
  if (!m) return null
  const n = Number(m[1])
  const unit = m[2]!.toLowerCase()[0]
  return unit === 'd' ? n : unit === 'w' ? n * 7 : unit === 'm' ? n * 30 : n * 365
}

/** The days a phrase means, relative to `today` (YYYY-MM-DD), or null if it is not a date. */
export function parseDatePhrase(phrase: string, today: string): DayRange | null {
  const s = phrase.trim().toLowerCase().replace(/\s+/g, ' ').replace(/,/g, '')
  if (s === '') return null
  if (s === 'today' || s === 'now') return one(today)
  if (s === 'yesterday') return one(addDays(today, -1))
  if (s === 'tomorrow') return one(addDays(today, 1))

  let m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(s)
  if (m) {
    const d = validDay(Number(m[1]), Number(m[2]), Number(m[3]))
    return d ? one(d) : null
  }
  m = /^(\d{4})-(\d{2})$/.exec(s)
  if (m) return Number(m[2]) >= 1 && Number(m[2]) <= 12 ? monthRange(Number(m[1]), Number(m[2])) : null
  m = /^(\d{4})$/.exec(s)
  if (m) return { from: `${m[1]}-01-01`, to: `${m[1]}-12-31` }

  m = /^(\d{1,4}) (day|week|month|year)s? ago$/.exec(s)
  if (m) {
    const n = Number(m[1])
    const unit = m[2]
    if (unit === 'day') return one(addDays(today, -n))
    if (unit === 'week') return one(addDays(today, -7 * n))
    if (unit === 'month') return one(addMonths(today, -n))
    return one(addMonths(today, -12 * n))
  }

  m = /^(this|last|next) (week|month|year)$/.exec(s)
  if (m) {
    const k = m[1] === 'this' ? 0 : m[1] === 'last' ? -1 : 1
    if (m[2] === 'week') return weekOf(addDays(today, 7 * k))
    const y = Number(today.slice(0, 4))
    if (m[2] === 'year') return { from: `${y + k}-01-01`, to: `${y + k}-12-31` }
    const first = addMonths(`${today.slice(0, 7)}-01`, k)
    return monthRange(Number(first.slice(0, 4)), Number(first.slice(5, 7)))
  }

  // Weekdays: "tuesday" = the most recent Tuesday (today counts), "last tuesday" = before today,
  // "next tuesday" = after today.
  m = /^(?:(last|next|this) )?([a-z]+)$/.exec(s)
  if (m) {
    const w = weekdayIndex(m[2]!)
    if (w >= 0) {
      const delta = (weekday(today) - w + 7) % 7
      if (m[1] === 'next') return one(addDays(today, ((w - weekday(today) + 7) % 7) || 7))
      if (m[1] === 'last') return one(addDays(today, -(delta || 7)))
      return one(addDays(today, -delta))
    }
    // "march": that month in the nearest year (past-leaning: a later month means last year).
    const mi = m[1] === undefined ? monthIndex(m[2]!) : -1
    if (mi >= 0) {
      const y = Number(today.slice(0, 4))
      return monthRange(mi + 1 > Number(today.slice(5, 7)) ? y - 1 : y, mi + 1)
    }
  }

  // "march 2026", "oct 9", "oct 9th 2026", "9 oct", "9 october 2026".
  m = /^([a-z]+) (\d{4})$/.exec(s)
  if (m && monthIndex(m[1]!) >= 0) return monthRange(Number(m[2]), monthIndex(m[1]!) + 1)
  m = /^([a-z]+) (\d{1,2})(?:st|nd|rd|th)?(?: (\d{4}))?$/.exec(s) ?? null
  let month = -1
  let dayN = 0
  let year: number | null = null
  if (m && monthIndex(m[1]!) >= 0) {
    month = monthIndex(m[1]!) + 1
    dayN = Number(m[2])
    year = m[3] ? Number(m[3]) : null
  } else {
    const r = /^(\d{1,2})(?:st|nd|rd|th)?\.? ([a-z]+)(?: (\d{4}))?$/.exec(s)
    if (r && monthIndex(r[2]!) >= 0) {
      month = monthIndex(r[2]!) + 1
      dayN = Number(r[1])
      year = r[3] ? Number(r[3]) : null
    }
  }
  if (month > 0) {
    const d = year !== null ? validDay(year, month, dayN) : nearestYear(month, dayN, today)
    return d ? one(d) : null
  }
  return null
}
